import { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Drawer,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import { Close as CloseIcon, KeyboardArrowDown as CollapseIcon, Science as BetaIcon } from "@mui/icons-material";
import { colors, typography, borders } from "../../ui/colors";
import { t as ct } from "../../dashboard/coach/coachTokens";
import WorkoutAiConversation from "./WorkoutAiConversation";
import WorkoutAiPreview from "./WorkoutAiPreview";
import { streamWorkoutAi } from "../../../services/WorkoutAiService";

const emptyPreview = () => ({ workouts: [] });

const CARDIO_TYPES = ['TREADMILL', 'RUNNING', 'BICYCLE_MACHINE', 'ELLIPTICAL_MACHINE', 'STEP_MACHINE', 'WALKING'];
const isCardio = (ex) => ex.targetMuscle === 'CARDIO' || CARDIO_TYPES.includes(ex.resistanceType);

/**
 * Convert an enriched template exercise (from NewTemplatePage state) to the AI preview shape.
 * The preview shape uses `sets/reps/restSec/exerciseType` so that AI markers and existing
 * exercises render identically in WorkoutAiPreview.
 */
const templateExerciseToPreview = (ex) => {
    if (isCardio(ex)) {
        return {
            exerciseType: "CARDIO",
            exerciseId: ex.exerciseId || ex.id,
            exerciseName: ex.exerciseName || ex.nameHeb || ex.name,
            imageUrl: ex.imageUrl || null,
            targetMuscle: ex.targetMuscle,
            resistanceType: ex.resistanceType,
            durationSec: ex.durationTime || 60,
        };
    }
    if (ex.catalyst) {
        return {
            exerciseType: "CATALYST",
            exerciseId: ex.exerciseId || ex.id,
            exerciseName: ex.exerciseName || ex.nameHeb || ex.name,
            imageUrl: ex.imageUrl || null,
            targetMuscle: ex.targetMuscle,
            resistanceType: ex.resistanceType,
            sets: ex.numberOfSets || 3,
            minDuration: ex.defaultMinDuration || 30,
            maxDuration: ex.defaultMaxDuration || 60,
            restSec: ex.restTimeInSec || 120,
        };
    }
    return {
        exerciseType: "STRENGTH",
        exerciseId: ex.exerciseId || ex.id,
        exerciseName: ex.exerciseName || ex.nameHeb || ex.name,
        imageUrl: ex.imageUrl || null,
        targetMuscle: ex.targetMuscle,
        resistanceType: ex.resistanceType,
        sets: ex.numberOfSets || 3,
        reps: ex.recommendedReps || 10,
        restSec: ex.restTimeInSec || 120,
        tempo: ex.tempo || "T1_0_1_0",
    };
};

/** Seed the AI preview from the existing template state. */
const seedPreviewFromTemplate = (template) => {
    if (!template?.workouts || template.workouts.length === 0) return emptyPreview();
    const workouts = template.workouts.map((w) => ({
        index: w.index,
        name: w.name || `Workout ${w.index + 1}`,
        nameHeb: w.nameHeb || w.name || `אימון ${w.index + 1}`,
        exercises: (w.exercises || []).map(templateExerciseToPreview),
    }));
    return { workouts };
};

const WorkoutAiDrawer = ({ open, onClose, template, programContext, onApply }) => {
    const [messages, setMessages] = useState([]);
    const [streamingText, setStreamingText] = useState("");
    const [statusText, setStatusText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [input, setInput] = useState("");
    const [preview, setPreview] = useState(emptyPreview());
    const [pendingQuestion, setPendingQuestion] = useState(null);
    const [applying, setApplying] = useState(false);
    const [confirmApply, setConfirmApply] = useState(false);
    const abortRef = useRef(null);
    const rawHistoryRef = useRef([]);

    const hasExistingWorkouts = (template?.workouts || []).length > 0;
    const templateId = template?.id || null;

    // Fresh session every time the drawer opens — but seed preview from existing template
    useEffect(() => {
        if (open) {
            setMessages([]);
            setStreamingText("");
            setStatusText("");
            setInput("");
            setPreview(seedPreviewFromTemplate(template));
            setPendingQuestion(null);
            setIsLoading(false);
            setApplying(false);
            rawHistoryRef.current = [];
        } else {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const applyMarkerToPreview = useCallback((marker) => {
        if (!marker || !marker.type) return;

        setPreview((prev) => {
            const next = { ...prev, workouts: [...(prev.workouts || [])] };

            switch (marker.type) {
                case "ADD_WORKOUT": {
                    next.workouts.push({
                        index: next.workouts.length,
                        name: marker.name || `Workout ${next.workouts.length + 1}`,
                        nameHeb: marker.nameHeb || marker.name || `אימון ${next.workouts.length + 1}`,
                        exercises: [],
                    });
                    return next;
                }
                case "ADD_EXERCISE":
                case "ADD_CARDIO":
                case "ADD_CATALYST": {
                    if (!marker.exercise) return prev;
                    const idx = typeof marker.workoutIdx === "number" ? marker.workoutIdx : next.workouts.length - 1;
                    if (idx < 0 || idx >= next.workouts.length) return prev;
                    const workout = { ...next.workouts[idx], exercises: [...(next.workouts[idx].exercises || [])] };
                    workout.exercises.push({
                        ...marker.exercise,
                        exerciseType: marker.exerciseType,
                        sets: marker.sets,
                        reps: marker.reps,
                        restSec: marker.restSec,
                        tempo: marker.tempo,
                        durationSec: marker.durationSec,
                        minDuration: marker.minDuration,
                        maxDuration: marker.maxDuration,
                    });
                    next.workouts[idx] = workout;
                    return next;
                }
                case "REPLACE_EXERCISE": {
                    if (!marker.exercise) return prev;
                    const wi = marker.workoutIdx;
                    const ei = marker.exerciseIdx;
                    if (wi == null || ei == null || !next.workouts[wi]?.exercises?.[ei]) return prev;
                    const workout = { ...next.workouts[wi], exercises: [...next.workouts[wi].exercises] };
                    workout.exercises[ei] = {
                        ...marker.exercise,
                        exerciseType: marker.exerciseType,
                        sets: marker.sets,
                        reps: marker.reps,
                        restSec: marker.restSec,
                        tempo: marker.tempo,
                    };
                    next.workouts[wi] = workout;
                    return next;
                }
                case "REMOVE_EXERCISE": {
                    const wi = marker.workoutIdx;
                    const ei = marker.exerciseIdx;
                    if (wi == null || ei == null || !next.workouts[wi]?.exercises) return prev;
                    const workout = { ...next.workouts[wi], exercises: next.workouts[wi].exercises.filter((_, i) => i !== ei) };
                    next.workouts[wi] = workout;
                    return next;
                }
                case "REMOVE_WORKOUT": {
                    const wi = marker.workoutIdx;
                    if (wi == null || wi < 0 || wi >= next.workouts.length) return prev;
                    next.workouts = next.workouts.filter((_, i) => i !== wi);
                    next.workouts.forEach((w, i) => { w.index = i; });
                    return next;
                }
                case "QUESTION": {
                    setPendingQuestion({ question: marker.question, options: marker.options || [] });
                    return prev;
                }
                case "DONE":
                default:
                    return prev;
            }
        });
    }, []);

    const sendMessage = useCallback((text) => {
        const message = (text ?? input).trim();
        if (!message || isLoading) return;

        setInput("");
        setPendingQuestion(null);
        setIsLoading(true);
        setStatusText("");
        setStreamingText("");

        const userMsg = { role: "user", content: message };
        const nextMessages = [...messages, userMsg];
        setMessages(nextMessages);

        abortRef.current = streamWorkoutAi(
            {
                message,
                history: [...rawHistoryRef.current],
                templateId,
                programContext,
                currentPreview: preview,
            },
            {
                onStatus: (s) => setStatusText(s),
                onToken: (token) => {
                    setStatusText("");
                    setStreamingText((prev) => prev + token);
                },
                onMarker: (marker) => {
                    applyMarkerToPreview(marker);
                },
                onSearchResults: (payload) => {
                    setStreamingText((prev) => {
                        if (prev.trim()) {
                            const cleaned = prev.replace(/\[(QUESTION|ADD_WORKOUT|ADD_EXERCISE|ADD_CARDIO|ADD_CATALYST|REPLACE_EXERCISE|REMOVE_EXERCISE|REMOVE_WORKOUT|SEARCH_EXERCISE|DONE)(?::[^\]]*)?\]/g, "").trim();
                            if (cleaned) {
                                setMessages((m) => [...m, { role: "assistant", content: cleaned }]);
                            }
                        }
                        return "";
                    });
                    setMessages((m) => [
                        ...m,
                        { role: "system", kind: "search_results", searches: payload?.searches || [] },
                    ]);
                },
                onComplete: (full) => {
                    const raw = full || "";
                    rawHistoryRef.current = [
                        ...rawHistoryRef.current,
                        { role: "user", content: message },
                        { role: "assistant", content: raw },
                    ];
                    const cleaned = raw.replace(/\[(QUESTION|ADD_WORKOUT|ADD_EXERCISE|ADD_CARDIO|ADD_CATALYST|REPLACE_EXERCISE|REMOVE_EXERCISE|REMOVE_WORKOUT|SEARCH_EXERCISE|DONE)(?::[^\]]*)?\]/g, "").trim();
                    if (cleaned) {
                        setMessages((prev) => [...prev, { role: "assistant", content: cleaned }]);
                    }
                    setStreamingText("");
                    setStatusText("");
                    setIsLoading(false);
                },
                onError: (err) => {
                    setStreamingText("");
                    setStatusText("");
                    setMessages((prev) => [
                        ...prev,
                        { role: "assistant", content: err || "אירעה שגיאה, נסה שוב.", isError: true },
                    ]);
                    setIsLoading(false);
                },
            }
        );
    }, [input, isLoading, messages, templateId, programContext, preview, applyMarkerToPreview]);

    const handleAnswerQuestion = useCallback((answer) => {
        setPendingQuestion(null);
        sendMessage(answer);
    }, [sendMessage]);

    const handleApplyClick = () => {
        if (hasExistingWorkouts) {
            // Existing template — preview already includes the existing workouts plus AI changes,
            // so "Replace" is the natural default. Still confirm to avoid accidental overwrites.
            setConfirmApply(true);
        } else {
            applyPreview("replace");
        }
    };

    const applyPreview = async (mode) => {
        setConfirmApply(false);
        setApplying(true);
        try {
            await onApply?.(preview, mode);
        } finally {
            setApplying(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                variant="temporary"
                transitionDuration={0}
                PaperProps={{
                    sx: {
                        height: "95vh",
                        direction: "rtl",
                        borderTopLeftRadius: borders.radius.lg,
                        borderTopRightRadius: borders.radius.lg,
                        overflow: "hidden",
                    },
                }}
                ModalProps={{ keepMounted: false }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    {/* Drawer header */}
                    <Box
                        sx={{
                            padding: "6px 12px",
                            borderBottom: borders.default,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bgcolor: colors.white,
                            flexShrink: 0,
                        }}
                    >
                        <Typography sx={{ ...typography.smallSemibold, color: colors.textSecondary }}>
                            בניית תוכנית אימון עם AI
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton onClick={onClose} size="small" title="צמצם">
                                <CollapseIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={onClose} size="small" title="סגור">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Experimental warning banner */}
                    <Box
                        sx={{
                            padding: "6px 14px",
                            bgcolor: colors.warningBg,
                            borderBottom: `1px solid ${colors.warning}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        <BetaIcon sx={{ fontSize: 16, color: colors.warningText }} />
                        <Typography sx={{ ...typography.tiny, color: colors.warningText }}>
                            <strong>בשלב ניסיוני (Beta):</strong> התוצאות של הבינה המלאכותית עלולות להיות לא מדויקות — עברו על התוכנית לפני החלה, ותקנו תרגילים, סטים וחזרות לפי הצורך.
                        </Typography>
                    </Box>

                    {/* Split layout */}
                    <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                        <Box sx={{ flex: 1, borderLeft: borders.default, minWidth: 0, minHeight: 0 }}>
                            <WorkoutAiConversation
                                messages={messages}
                                streamingText={streamingText}
                                statusText={statusText}
                                isLoading={isLoading}
                                pendingQuestion={pendingQuestion}
                                input={input}
                                onInputChange={setInput}
                                onSend={() => sendMessage()}
                                onAnswerQuestion={handleAnswerQuestion}
                            />
                        </Box>
                        <Box sx={{ flex: 1.1, minWidth: 0, minHeight: 0 }}>
                            <WorkoutAiPreview
                                preview={preview}
                                onApply={handleApplyClick}
                                applying={applying}
                                hasExistingWorkouts={hasExistingWorkouts}
                            />
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={confirmApply}
                onClose={() => setConfirmApply(false)}
                PaperProps={{ sx: { direction: "rtl", borderRadius: borders.radius.md } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>החלת התוכנית על התבנית</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ direction: "rtl" }}>
                        בחר איך להחיל את התצוגה המקדימה על התבנית.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmApply(false)} sx={{ textTransform: "none" }}>
                        ביטול
                    </Button>
                    <Button onClick={() => applyPreview("merge")} sx={{ textTransform: "none" }}>
                        מזג (הוסף לימי האימון הקיימים)
                    </Button>
                    <Button
                        onClick={() => applyPreview("replace")}
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            bgcolor: ct.green,
                            "&:hover": { bgcolor: ct.greenDark },
                        }}
                    >
                        החלף את הקיים
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WorkoutAiDrawer;
